import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { usePortalAuth, formatPortalError } from "../../lib/portal-auth-context";
import { PageHeader } from "./portal-ui";
import { PaperPlaneRight } from "@phosphor-icons/react";

const CATEGORIES = [
  "Guantes",
  "Jeringas",
  "Gasas",
  "Vendajes",
  "EPP",
  "IV",
  "Suturas",
];

export default function PortalNewQuote() {
  const { api } = usePortalAuth();
  const nav = useNavigate();
  const { state } = useLocation();
  const prefillSku = state?.prefillSku;
  const prefillName = state?.prefillName;

  const [selected, setSelected] = useState(
    prefillSku ? [prefillName || prefillSku] : []
  );
  const [message, setMessage] = useState(
    prefillSku
      ? `Me interesa cotizar el producto ${prefillName || prefillSku} (SKU: ${prefillSku}). Volumen: `
      : ""
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const toggle = (c) =>
    setSelected((s) =>
      s.includes(c) ? s.filter((x) => x !== c) : [...s, c]
    );

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/quotes", {
        products: selected,
        message: message.trim(),
      });
      toast.success("Solicitud enviada a tu asesor ICOR");
      nav("/portal/quotes");
    } catch (err) {
      setError(formatPortalError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="portal-new-quote-page">
      <PageHeader
        eyebrow="Nueva solicitud"
        title="Solicita una cotización"
        subtitle="Describe tu requerimiento. Un asesor ICOR responderá en menos de 24h."
      />

      <form
        onSubmit={submit}
        className="bg-white border border-slate-200 p-6 md:p-8 max-w-2xl"
        style={{ borderRadius: 4 }}
        data-testid="portal-new-quote-form"
      >
        <label className="block text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 mb-3">
          Categorías de interés
        </label>
        <div className="flex flex-wrap gap-2 mb-6">
          {CATEGORIES.map((c) => {
            const on = selected.includes(c);
            return (
              <button
                type="button"
                key={c}
                onClick={() => toggle(c)}
                data-testid={`portal-new-quote-cat-${c}`}
                className={`px-3 py-2 text-xs uppercase tracking-[0.18em] font-semibold border transition-colors ${
                  on
                    ? "bg-[var(--icor-blue)] text-white border-[var(--icor-blue)]"
                    : "bg-white text-slate-600 border-slate-300 hover:border-[var(--icor-blue)]"
                }`}
                style={{ borderRadius: 4 }}
              >
                {c}
              </button>
            );
          })}
        </div>

        <label className="block text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2">
          Detalle del requerimiento
        </label>
        <textarea
          required
          rows={6}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Ej. 5,000 cajas de guantes nitrilo M, entrega en CDMX..."
          className="icor-input resize-none"
          data-testid="portal-new-quote-message"
        />

        {error && (
          <div
            data-testid="portal-new-quote-error"
            className="mt-5 p-3 text-sm text-red-700 bg-red-50 border border-red-200"
            style={{ borderRadius: 4 }}
          >
            {error}
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary disabled:opacity-60"
            data-testid="portal-new-quote-submit"
          >
            {submitting ? "Enviando..." : "Enviar solicitud"}
            <PaperPlaneRight size={16} />
          </button>
        </div>
      </form>
    </div>
  );
}
